// Item 373 — delete protection on the client side.
//
// A camera delete, a group delete and a superuser's delete of a user that owns cameras carry
// `{ master_password }` in the request body; the API answers one of four codes when it refuses.
// `masterPasswordRefusal` reads that answer off an axios error; `deleteOneByOne` runs a list of
// deletes in sequence and stops at the first error, so one typo costs one wrong try, not one per
// camera, and a lock mid-way never leaves half the list in flight.

export type MasterPasswordCode =
  | 'master_password_required'
  | 'master_password_invalid'
  | 'master_password_locked'
  | 'master_password_not_set'

export interface MasterPasswordRefusal {
  code: MasterPasswordCode
  detail: string
  /** Seconds to wait, only with `master_password_locked`. */
  retryAfter: number | undefined
}

const CODES: ReadonlySet<string> = new Set<MasterPasswordCode>([
  'master_password_required', 'master_password_invalid', 'master_password_locked', 'master_password_not_set',
])

export function masterPasswordRefusal(err: unknown): MasterPasswordRefusal | null {
  const response = (err as { response?: { status?: number; data?: Record<string, unknown> } } | undefined)?.response
  if (response?.status !== 403 && response?.status !== 429) return null
  const data = response.data
  const code = data?.code
  if (typeof code !== 'string' || !CODES.has(code)) return null
  const retry = data?.retry_after
  return {
    code: code as MasterPasswordCode,
    detail: typeof data?.detail === 'string' ? data.detail : '',
    retryAfter: typeof retry === 'number' ? retry : (typeof retry === 'string' && retry !== '' ? Number(retry) : undefined),
  }
}

export interface OneByOneResult {
  /** The ids whose delete succeeded, in order, before the first error. */
  deleted: number[]
  refusal: MasterPasswordRefusal | null
  /** Any other error that stopped the run. */
  error: unknown | null
}

/** The selection after a partial run: the ids that were deleted leave it, so a retry sends the rest only. */
export function withoutDeleted(selected: Set<number>, deleted: number[]): Set<number> {
  const rest = new Set(selected)
  for (const id of deleted) rest.delete(id)
  return rest
}

export async function deleteOneByOne(ids: number[], del: (id: number) => Promise<unknown>): Promise<OneByOneResult> {
  const deleted: number[] = []
  for (const id of ids) {
    try {
      await del(id)
      deleted.push(id)
    } catch (err) {
      const refusal = masterPasswordRefusal(err)
      return { deleted, refusal, error: refusal ? null : err }
    }
  }
  return { deleted, refusal: null, error: null }
}
